/**
 * The roll-over — a section scrolls over the one before it.
 *
 * A `[data-section]` that takes part is `position: sticky`; which ones those
 * are the CSS decides (20_global.sass, from the body class glunz_stack_scope()
 * prints — by default only the reference slides). Their `top` is
 * `min(0, viewport − height)`, so such a section freezes once it has been read
 * completely (its bottom at the viewport bottom) and the next section slides
 * in on top of it. Sections shorter than the viewport are stretched to
 * 100svh by CSS, so `top` is never positive.
 *
 * Sections that do not stick are skipped — they need no offset, and growing in
 * place moves what follows them rather than themselves, so the compensation
 * below would be wrong for them.
 *
 * When a frozen section changes height (an accordion opens), the sticky
 * offset would make it jump upwards by the growth. The scroll position is
 * compensated so the content the user is looking at stays where it is. Growth
 * the section's own negative margin takes back — the lift of a reference
 * slide (85_blocks.sass) — moves nothing and is left alone.
 */
import { flowTop } from './scroll'

const sections = [...document.querySelectorAll('[data-section]')]
const sizes = new WeakMap()

const update = (entries = []) => {
  const viewport = window.innerHeight
  const scrollY = window.scrollY
  let compensate = 0

  for (const section of sections) {
    // Read from the CSS on every pass: the scope is a body class, and a
    // breakpoint may take a section out of the stack at any width.
    const style = getComputedStyle(section)
    if (style.position !== 'sticky') {
      sizes.delete(section)
      continue
    }

    const height = section.offsetHeight
    // The space the section takes in the flow, i.e. what it pushes down
    const flow = height + parseFloat(style.marginBottom)
    const previous = sizes.get(section)
    sizes.set(section, { height, flow })

    if (
      previous !== undefined &&
      previous.flow !== flow &&
      entries.some((e) => e.target === section)
    ) {
      const top = flowTop(section) - scrollY
      const stuckTop = viewport - previous.height
      // Was the section frozen (displaced downwards by its sticky offset)?
      if (top < stuckTop - 0.5) {
        compensate = Math.max(compensate, stuckTop - top)
      }
    }

    section.style.setProperty('--stack-top', `${Math.min(0, viewport - height)}px`)
  }

  if (compensate > 0) {
    window.scrollBy({ top: -compensate, behavior: 'instant' })
  }
}

if (sections.length) {
  const observer = new ResizeObserver((entries) => update(entries))
  sections.forEach((section) => observer.observe(section))
  window.addEventListener('resize', () => update())
  window.addEventListener('load', () => update())
  update()
}
